<template>
  <div>
    <v-text-field
      v-model="state.userName"
      required
      variant="outlined"
      density="compact"
      v-model:error-messages="fieldErrors.userName"
      :class="{ text__error: fieldErrors.userName }"
      prepend-inner-icon="mdi-database"
      label="Site ID"></v-text-field>
    <v-text-field
      v-model="state.password"
      required
      :class="{ text__error: fieldErrors.password }"
      v-model:error-messages="fieldErrors.password"
      variant="outlined"
      density="compact"
      prepend-inner-icon="mdi-key"
      label="WS API Key"></v-text-field>

    <v-switch
      class="mt-0"
      v-model="state.useTestEmails"
      label="Use @example for all carrier emails"
      color="secondary"
      hide-details></v-switch>

    <v-expansion-panels class="my-4">
      <v-expansion-panel title="Advanced Options">
        <v-expansion-panel-text>
          <v-switch
            class="pt-0 mt-0"
            v-model="state.addCustomStatusTag"
            label="Add custom statuses as a tag"
            color="secondary"
            hide-details></v-switch>
          <p class="font-size-x-small px-4 opacity-60">
            Enabling this creates any custom status on the appointments as a tag
          </p>
        </v-expansion-panel-text>

        <v-expansion-panel-text>
          <v-select
            class="mb-6"
            persistent-hint
            hint="This is to allow for selection of a secondary Legacy URL if the dedicated Opendock URL does not work properly."
            required
            use-object
            density="compact"
            label="Select Web Services URL"
            variant="outlined"
            :items="appointmentPlusUrls"
            v-model="state.selectedUrl"></v-select>

          <v-text-field
            class="mb-6"
            v-model="state.apptRequestChunkSize"
            required
            type="number"
            :class="{ text__error: fieldErrors.apptRequestChunkSize }"
            v-model:error-messages="fieldErrors.apptRequestChunkSize"
            variant="outlined"
            density="compact"
            persistent-hint
            hint="Legacy has issues fetching large amounts of appointments.  This setting breaks up each request into x amount of days at a time so you have more control if there is a failure"
            label="Appointment Request Chunk Size (in Days)"></v-text-field>

          <h6>Appointment Export Date Range</h6>
          <div
            class="v-input v-input--density-compact v-input--dirty v-input--readonly v-text-field v-select v-select--single">
            <div class="input__control">
              <div
                class="v-field v-field--active v-field--appended v-field--dirty v-field--has-label v-field--variant-outlined v-theme--opendockTheme">
                <div class="v-field__field">
                  <VueDatePicker
                    name="start-date-picker"
                    auto-apply
                    :enable-time-picker="false"
                    :month-change-on-scroll="false"
                    :text-input-options="{
                      enterSubmit: true,
                      tabSubmit: true,
                      format: 'MM/dd/yyyy'
                    }"
                    class="date-picker"
                    v-model="startDate"></VueDatePicker>
                </div>
                <div class="v-field__outline">
                  <div class="v-field__outline__start"></div>
                  <div class="v-field__outline__notch">
                    <label class="v-label v-field-label v-field-label--floating" aria-hidden="true">
                      Appointment Export Start Date
                    </label>
                  </div>
                  <div class="v-field__outline__end"></div>
                </div>
              </div>
            </div>
          </div>

          <div
            class="v-input v-input--density-compact v-input--dirty v-input--readonly v-text-field v-select v-select--single">
            <div class="input__control">
              <div
                class="v-field v-field--active v-field--appended v-field--dirty v-field--has-label v-field--variant-outlined v-theme--opendockTheme">
                <div class="v-field__field">
                  <VueDatePicker
                    name="end-date-picker"
                    auto-apply
                    :enable-time-picker="false"
                    :month-change-on-scroll="false"
                    :text-input-options="{
                      enterSubmit: true,
                      tabSubmit: true,
                      format: 'MM/dd/yyyy'
                    }"
                    class="date-picker"
                    v-model="endDate"></VueDatePicker>
                </div>
                <div class="v-field__outline">
                  <div class="v-field__outline__start"></div>
                  <div class="v-field__outline__notch">
                    <label class="v-label v-field-label v-field-label--floating" aria-hidden="true">
                      Appointment Export End Date
                    </label>
                  </div>
                  <div class="v-field__outline__end"></div>
                </div>
              </div>
            </div>
          </div>
        </v-expansion-panel-text>
      </v-expansion-panel>
    </v-expansion-panels>

    <div class="d-flex justify-center justify-self-end">
      <warehouse-select-dialog
        :warehouses="state.warehouses"
        :is-dialog-visible="state.shouldShowDialog"
        @load-warehouses="loadWarehouses"
        :loading="state.loading"
        @export-data="exportData"
        @close="close"></warehouse-select-dialog>
    </div>
    <progress-dialog
      title="Exporting Data From AppointmentPlus"
      :should-show-progress-dialog="progress.shouldShowProgressDialog"
      :current-step-message="progress.currentStepMessage"
      :current-perc="progress.currentPerc"></progress-dialog>
  </div>
</template>

<script lang="ts" setup>
import VueDatePicker from '@vuepic/vue-datepicker';
import '@vuepic/vue-datepicker/dist/main.css';
import { onMounted, reactive, ref, watch } from 'vue';
import { APIs } from '@/enums/APIs';
import { getClient } from '@/services/clientService';
import type { GenericArrayOfObjects, INormalizedResponse } from '@/interfaces/api';
import type { INovaAppointment, INovaDock, INovaLoadType, INovaWarehouse } from '@/interfaces/nova';
import { exportWorkbook, type IExportedData } from '@/services/workbookService';
import { AppointmentPlusApiService } from '@/services/appointment_plus/AppointmentPlusApiService';
import {
  cleanWhiteSpace,
  EMAIL_SEPARATION_REGEX,
  flashErrorsIfExist,
  makeUniqueObjectArray,
  systemNotify
} from '@/services/util';
import { appointmentPlusUrls } from '@/clients/appointment_plus/WSClient';
import { DateTime } from 'luxon';
import { AppointmentPlusDateFormats } from '@/enums/appointmentplus/appointmentplusEnums';
import WarehouseSelectDialog from '@/components/dialogs/WarehouseSelectDialog.vue';
import ProgressDialog from '@/components/dialogs/ProgressDialog.vue';
import { useProgress } from '@/composables/progressBar';
import config from '@/config/config';

const APPT_RANGE_START_DAYS_FROM_NOW = 1;
const APPT_RANGE_END_DAYS_FROM_NOW = 365;

const startDate = ref();
const endDate = ref();

const state = reactive({
  userName: '',
  password: '',
  loading: false,
  shouldShowDialog: false,
  warehouses: [] as GenericArrayOfObjects,
  useTestEmails: config.use_test_emails ? Boolean(config.use_test_emails) : true,
  selectedUrl: appointmentPlusUrls[0]?.value, // Use the first value
  apptRequestChunkSize: APPT_RANGE_END_DAYS_FROM_NOW,
  addCustomStatusTag: false
});

const TESTING_EMAIL_DOMAIN = '@example.com';

const progress = reactive(useProgress());

// EXPORT
const exportData = async (warehousesToExport: string[]) => {
  if (!isValid()) {
    return;
  }

  const APPT_RANGE_START_LUXON = startDate.value
    ? DateTime.fromJSDate(startDate.value).startOf('day')
    : DateTime.now().plus({ days: APPT_RANGE_START_DAYS_FROM_NOW }).startOf('day');
  const APPT_RANGE_END_LUXON = endDate.value
    ? DateTime.fromJSDate(endDate.value).endOf('day')
    : DateTime.now().plus({ days: APPT_RANGE_END_DAYS_FROM_NOW });

  state.loading = true;
  const apiService = getClient({
    api: APIs.AP,
    credentials: {
      userName: state.userName.trim(),
      password: state.password.trim()
    },
    url: state.selectedUrl
  }) as AppointmentPlusApiService;

  const exportedData: IExportedData = {
    warehouses: [],
    loadTypes: [],
    docks: [],
    appointments: []
  };

  // GET WAREHOUSES
  progress.setDialogVisibility(true);
  progress.createInterval('Fetching Warehouses', 15);
  try {
    const warehouseResponse: INormalizedResponse = await apiService.getWarehouses({
      location_id: warehousesToExport.join(',')
    });
    if (flashErrorsIfExist(warehouseResponse)) {
      abortExportProcess();
      return false;
    }
    if (state.useTestEmails) {
      warehouseResponse.data = warehouseResponse.data.map(warehouse => {
        if (!cleanWhiteSpace(warehouse.email)) {
          warehouse.email = 'warehouse_placeholder';
        }

        const newEmails = getTestEmails(warehouse.email);
        warehouse.email = newEmails?.[0] ?? '';

        return warehouse;
      });
    }

    exportedData.warehouses = warehouseResponse.data as INovaWarehouse[];
  } catch (e) {
    abortExportProcess();
    return false;
  }

  // GET DOCKS
  await progress.zoom(15);
  progress.createInterval('Fetching Docks', 40);
  try {
    const dockPromises: Promise<INormalizedResponse>[] = warehousesToExport.map(async warehouse => {
      return apiService.getDocks({
        status: 'active',
        c_id: warehouse
      });
    });

    let docks: INovaDock[] = [];
    let errors = false;

    await Promise.all(dockPromises).then(responses => {
      responses.forEach(response => {
        if (flashErrorsIfExist(response)) {
          errors = true;
          abortExportProcess();
          return false;
        }
        docks = [...docks, ...(response.data as INovaDock[])];
      });
    });

    if (errors) {
      return;
    }

    docks = makeUniqueObjectArray(docks) as INovaDock[];

    exportedData.docks = docks.filter(dock => {
      return warehousesToExport.includes(dock.externalWarehouseId);
    }) as INovaDock[];
  } catch (error: any) {
    systemNotify({
      message: error.message,
      type: 'error'
    });
    return abortExportProcess();
  }

  // GET LOAD TYPES
  await progress.zoom(40);
  progress.createInterval('Fetching Load Types', 60);
  let loadTypesArr = [] as string[];
  exportedData.docks.forEach(dock => {
    loadTypesArr = [...loadTypesArr, ...JSON.parse(dock.externalLoadTypeIds)];
  });
  const loadTypes = Array.from(new Set(loadTypesArr));
  try {
    let loadTypeObjArr: INovaLoadType[] = [];
    if (loadTypes.length > 0) {
      const loadTypesResponse: INormalizedResponse = await apiService.getLoadTypes({
        service_id: loadTypes.join(',')
      });
      if (flashErrorsIfExist(loadTypesResponse)) {
        abortExportProcess();
        return false;
      }
      loadTypeObjArr = loadTypesResponse.data as INovaLoadType[];
    }
    exportedData.loadTypes = loadTypeObjArr;
  } catch (error: any) {
    systemNotify({
      message: error.message,
      type: 'error'
    });
    return abortExportProcess();
  }

  // GET APPOINTMENTS
  await progress.zoom(60);
  progress.createInterval('Fetching Appointments', 98);
  try {
    let startIterator = APPT_RANGE_START_LUXON;
    let appointmentResponses: INormalizedResponse[] = [];
    while (startIterator <= APPT_RANGE_END_LUXON) {
      const newResponses = await Promise.all(
        warehousesToExport.map(async warehouseId => {
          const appointmentResponse: INormalizedResponse = await apiService.getAppointments(
            {
              start_date: startIterator.toFormat(AppointmentPlusDateFormats.API),
              end_date: startIterator
                .plus({ day: state.apptRequestChunkSize })
                .toFormat(AppointmentPlusDateFormats.API),
              location_id: warehouseId
            },
            exportedData.warehouses as INovaWarehouse[],
            { addCustomStatusAsTag: state.addCustomStatusTag }
          );

          return appointmentResponse;
        })
      );
      appointmentResponses = [...appointmentResponses, ...newResponses];
      startIterator = startIterator.plus({ day: state.apptRequestChunkSize + 1 });
    }

    let appointments: GenericArrayOfObjects = [];
    let errors = false;
    appointmentResponses.forEach(response => {
      if (flashErrorsIfExist(response)) {
        errors = true;
      }
      if (state.useTestEmails) {
        response.data = response.data.map(appointment => {
          if (!cleanWhiteSpace(appointment.email)) {
            appointment.email = 'placeholder';
          }

          appointment.email = getTestEmail(appointment.email);
          const newEmails = getTestEmails(appointment.ccEmails);
          appointment.ccEmails = newEmails.join(',');

          return appointment;
        });
      }

      appointments = [...appointments, ...response.data];
    });

    if (errors) {
      abortExportProcess();
      return false;
    }

    exportedData.appointments = appointments as INovaAppointment[];
  } catch (error: any) {
    systemNotify({
      message: error.message,
      type: 'error'
    });
    abortExportProcess();
    return false;
  }

  exportWorkbook(exportedData, `${APIs.AP} export - ${state.userName}`);
  state.loading = false;
  state.warehouses = [];
  progress.clear();
  await progress.zoom(99, 5);
  progress.update('All done!', 100);
  setTimeout(() => {
    progress.setDialogVisibility(false);
    progress.update('', 0);
  }, 2000);
};

const getTestEmails = (originalEmails: string) => {
  let emails: string[] = originalEmails.split(EMAIL_SEPARATION_REGEX);
  const newEmails: string[] = [];
  if (emails.length > 0 && emails[0]) {
    emails.forEach((email: string) => {
      newEmails.push(getTestEmail(email));
    });
  }
  return newEmails;
};

const getTestEmail = (email: string) => {
  const emailParts = email.split('@');
  return `${emailParts[0]}${TESTING_EMAIL_DOMAIN}`;
};

const abortExportProcess = () => {
  state.loading = false;
  state.shouldShowDialog = false;
  progress.setDialogVisibility(false);
  progress.clear();
  progress.update('', 0);
};

const close = (shouldClearWarehouses = true) => {
  state.shouldShowDialog = false;
  if (shouldClearWarehouses) {
    state.warehouses = [];
  }
};

const loadWarehouses = async () => {
  const apiService = getClient({
    api: APIs.AP,
    credentials: {
      userName: state.userName.trim(),
      password: state.password.trim()
    },
    url: state.selectedUrl
  }) as AppointmentPlusApiService;
  state.loading = true;
  const warehouseResponse: INormalizedResponse = await apiService.getWarehouses({}, false);
  state.loading = false;
  if (flashErrorsIfExist(warehouseResponse)) {
    abortExportProcess();
    return false;
  }
  state.warehouses = warehouseResponse.data;
  state.shouldShowDialog = true;
};

onMounted(() => {
  startDate.value = DateTime.now().plus({ day: APPT_RANGE_START_DAYS_FROM_NOW }).toJSDate();
  endDate.value = DateTime.now().plus({ day: APPT_RANGE_END_DAYS_FROM_NOW }).toJSDate();
});

// VALIDATION
const fieldErrors = reactive({
  userName: '',
  password: '',
  apptRequestChunkSize: ''
});
const errorMessages = {
  userName: 'A Site ID is required',
  password: 'A Web Services API Key is required',
  apptRequestChunkSize: 'Chunk size must be a positive whole number less than or equal to 365'
};
const isValid = () => {
  if (!state.userName) {
    fieldErrors.userName = errorMessages.userName;
  }
  if (!state.password) {
    fieldErrors.password = errorMessages.password;
  }
  if (!state.apptRequestChunkSize) {
    fieldErrors.apptRequestChunkSize = errorMessages.apptRequestChunkSize;
  }
  if (fieldErrors.userName || fieldErrors.password) {
    return false;
  }
  if (fieldErrors.apptRequestChunkSize) {
    return false;
  }

  return true;
};

watch(
  state,
  newState => {
    if (newState.userName) {
      fieldErrors.userName = '';
    } else {
      fieldErrors.userName = errorMessages.userName;
    }
    if (newState.password) {
      fieldErrors.password = '';
    } else {
      fieldErrors.password = errorMessages.password;
    }
    if (newState.apptRequestChunkSize) {
      if (newState.apptRequestChunkSize < 1 || newState.apptRequestChunkSize > 365) {
        fieldErrors.apptRequestChunkSize = errorMessages.apptRequestChunkSize;
      } else {
        fieldErrors.apptRequestChunkSize = '';
      }
    } else {
      fieldErrors.apptRequestChunkSize = errorMessages.apptRequestChunkSize;
    }
  },
  { deep: true }
);
</script>

<style lang="scss">
.input__control {
  z-index: 1;
  position: relative;
}

.v-expansion-panels {
  z-index: 100 !important;
}

[name='start-date-picker'],
[name='end-date-picker'] {
  border: none;
  padding-top: 8px;
}
</style>
